import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PROJECT_TYPES, PROJECT_TYPE_LABEL, type ProjectType } from '@allemnii-pulse/shared';
import { api } from '../../lib/api.js';
import { useAuth } from '../auth/AuthProvider.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { DatePicker } from '../../components/ui/DatePicker.js';
import { Dialog, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select.js';

export const CLIENT_FOLDER_LABEL = 'Client folder';

export function NewProjectDialog({
  orgId,
  open,
  onClose,
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [projectType, setProjectType] = useState<ProjectType>('web_dev');
  const [otherProjectTypeLabel, setOtherProjectTypeLabel] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [driveLink, setDriveLink] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOther = projectType === 'other';
  const driveUrl = driveLink.trim();
  const driveLinkValid = !driveUrl || URL.canParse(driveUrl);
  const canSubmit = name.trim() && (!isOther || otherProjectTypeLabel.trim()) && driveLinkValid;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !user) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.post<{ id: string }>(`/orgs/${orgId}/projects`, {
        name: name.trim(),
        projectType,
        ...(isOther ? { otherProjectTypeLabel: otherProjectTypeLabel.trim() } : {}),
        startDate,
        leadUid: user.uid,
      });
      if (driveUrl) {
        // Best-effort: the project exists either way, and the link can be added from Deliverables.
        await api
          .post(`/projects/${created.id}/deliverables`, {
            kind: 'client_folder',
            label: CLIENT_FOLDER_LABEL,
            url: driveUrl,
          })
          .catch(() => undefined);
      }
      onClose();
      navigate(`/projects/${created.id}`);
    } catch {
      setError('Could not create the project. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>New project</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="project-name">Name</Label>
          <Input
            id="project-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="project-type">Project type</Label>
          <Select value={projectType} onValueChange={(v) => setProjectType(v as ProjectType)}>
            <SelectTrigger id="project-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROJECT_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {PROJECT_TYPE_LABEL[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isOther && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-type-other">What kind of project is it?</Label>
            <Input
              id="project-type-other"
              autoFocus
              value={otherProjectTypeLabel}
              placeholder="e.g. Data pipeline, mobile app, internal tooling…"
              onChange={(e) => setOtherProjectTypeLabel(e.target.value)}
            />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="project-start">Start date</Label>
          <DatePicker id="project-start" value={startDate} onChange={setStartDate} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="project-drive">Client folder (Google Drive link, optional)</Label>
          <Input
            id="project-drive"
            type="url"
            value={driveLink}
            placeholder="https://drive.google.com/drive/folders/…"
            onChange={(e) => setDriveLink(e.target.value)}
          />
          {!driveLinkValid && (
            <p className="text-xs text-danger">Enter a full link starting with https://</p>
          )}
          <p className="text-xs text-muted">
            Saved to the project's Deliverables as “Client folder”.
          </p>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <DialogFooter className="flex-wrap">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="default" disabled={submitting || !canSubmit}>
            {submitting ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
